<script setup>
import { computed, onMounted, ref } from 'vue'
import { api } from '../api'
import { useI18n } from '../i18n'
import { auditEventLabel, formatDate } from '../monitoring'

const { locale, t } = useI18n()
const response = ref({ events: [], actors: [], total: 0, page: 1, pageSize: 25 })
const loading = ref(true)
const error = ref('')
const filters = ref({ category: '', actor: '', from: '', to: '', q: '' })

const categoryOptions = [
  ['auth', 'categoryAuth'], ['users', 'categoryUsers'], ['stations', 'categoryStations'],
  ['reports', 'categoryReports'], ['settings', 'categorySettings'], ['catalog', 'categoryCatalog'],
]
const totalPages = computed(() => Math.max(1, Math.ceil(response.value.total / response.value.pageSize)))
const firstItem = computed(() => response.value.total ? ((response.value.page - 1) * response.value.pageSize) + 1 : 0)
const lastItem = computed(() => Math.min(response.value.total, response.value.page * response.value.pageSize))

function categoryLabel(eventType) {
  if (['login', 'logout', 'password_changed'].includes(eventType)) return t('categoryAuth')
  if (['bootstrap_admin_created', 'users_listed', 'user_created', 'user_blocked', 'user_unblocked', 'user_deleted'].includes(eventType)) return t('categoryUsers')
  if (['stations_listed', 'station_created', 'station_test_succeeded', 'station_test_failed', 'station_deleted', 'active_station_changed'].includes(eventType)) return t('categoryStations')
  if (['report_exported', 'report_export_failed', 'chart_rendered', 'chart_render_failed', 'chart_exported', 'chart_export_failed', 'template_parameters_loaded', 'template_parameters_failed'].includes(eventType)) return t('categoryReports')
  if (['report_language_changed', 'audit_retention_changed'].includes(eventType)) return t('categorySettings')
  return t('categoryCatalog')
}

async function load(page = response.value.page) {
  loading.value = true
  error.value = ''
  try {
    const params = new URLSearchParams({ page: String(page), pageSize: '25' })
    for (const [name, value] of Object.entries(filters.value)) {
      if (value) params.set(name, value)
    }
    response.value = await api(`/admin/audit-events?${params.toString()}`)
  } catch (cause) {
    error.value = cause.message || t('auditSettingsFailed')
  } finally {
    loading.value = false
  }
}

function applyFilters() {
  load(1)
}

function resetFilters() {
  filters.value = { category: '', actor: '', from: '', to: '', q: '' }
  load(1)
}

onMounted(() => load(1))
</script>

<template>
  <section class="history-page">
    <RouterLink class="back-link" :to="{ name: 'admin-monitoring' }">{{ t('backToMonitoring') }}</RouterLink>
    <div class="page-title"><div><p class="eyebrow">{{ t('settings') }}</p><h1>{{ t('auditHistoryTitle') }}</h1><p class="muted">{{ t('auditHistoryHint') }}</p></div></div>

    <section class="card audit-history-controls">
      <form class="audit-filter-grid" @submit.prevent="applyFilters">
        <label>{{ t('auditCategory') }}<select v-model="filters.category"><option value="">{{ t('allCategories') }}</option><option v-for="[value, label] in categoryOptions" :key="value" :value="value">{{ t(label) }}</option></select></label>
        <label>{{ t('auditActor') }}<select v-model="filters.actor"><option value="">{{ t('allUsers') }}</option><option v-for="actor in response.actors" :key="actor" :value="actor">{{ actor }}</option></select></label>
        <label>{{ t('fromDate') }}<input v-model="filters.from" type="date" /></label>
        <label>{{ t('toDate') }}<input v-model="filters.to" type="date" /></label>
        <label class="audit-search-field">{{ t('searchAudit') }}<input v-model.trim="filters.q" type="search" :placeholder="t('searchAuditPlaceholder')" /></label>
        <div class="audit-filter-actions"><button class="button button-small" type="submit">{{ t('applyFilters') }}</button><button class="button button-small button-secondary" type="button" @click="resetFilters">{{ t('resetFilters') }}</button></div>
      </form>
    </section>

    <p v-if="error" class="notice error" role="alert">{{ error }}</p>
    <p v-else-if="loading" class="muted" aria-live="polite">{{ t('loading') }}</p>
    <section v-else class="card history-table-panel">
      <div class="section-heading"><p class="muted">{{ t('showing', { from: firstItem, to: lastItem, total: response.total }) }}</p></div>
      <p v-if="!response.events.length" class="muted">{{ t('noAuditEvents') }}</p>
      <div v-else class="table-wrap"><table><thead><tr><th>{{ t('started') }}</th><th>{{ t('auditActor') }}</th><th>{{ t('auditCategory') }}</th><th>{{ t('auditAction') }}</th><th>{{ t('auditDetails') }}</th></tr></thead><tbody>
        <tr v-for="event in response.events" :key="event.id"><td>{{ formatDate(locale, event.createdAt) }}</td><td>{{ event.actor }}</td><td>{{ categoryLabel(event.eventType) }}</td><td><strong>{{ auditEventLabel(t, event.eventType) }}</strong></td><td>{{ event.detail || '—' }}</td></tr>
      </tbody></table></div>
      <nav v-if="response.total > response.pageSize" class="history-pagination" :aria-label="t('pageNavigation')"><button class="catalog-page" type="button" :disabled="response.page <= 1" @click="load(response.page - 1)">‹ {{ t('previous') }}</button><span>{{ t('pageOf', { current: response.page, total: totalPages }) }}</span><button class="catalog-page" type="button" :disabled="response.page >= totalPages" @click="load(response.page + 1)">{{ t('next') }} ›</button></nav>
    </section>
  </section>
</template>
